import { AppHeader } from "./AppHeader";
import { BreadcrumbProvider } from "./BreadcrumbContext";
import { MobileBar, PrimaryNav } from "./PrimaryNav";
import ChatPanel, { ChatExpandCtx } from "../assistant/chat-panel";
import { useCallback, useState } from "react";
import { useAuth } from "@/hooks/useAuth";

interface AppLayoutProps {
  children: React.ReactNode;
}

// The signed-in frame, as in the Dashboard: the left menu | the page. Pages
// scroll inside the content column; Ela's chat floats over it.
export function AppLayout({ children }: AppLayoutProps) {
  const { user } = useAuth();
  // Myela chat visibility mirrors the API's MYELA_ENABLED (build-time VITE_*, default off).
  const showChat =
    import.meta.env.VITE_MYELA_ENABLED === "true" &&
    user?.userType !== "GODADDY_MERCHANT";

  const [chatExpanded, setChatExpanded] = useState(false);
  // A question from Home's ask bar, waiting for the chat to send it.
  const [pendingAsk, setPendingAsk] = useState<string | null>(null);
  const ask = useCallback((text: string) => {
    setPendingAsk(text);
    setChatExpanded(true);
  }, []);
  const collapse = useCallback(() => setChatExpanded(false), []);

  return (
    <ChatExpandCtx.Provider
      value={{
        expanded: chatExpanded,
        setExpanded: setChatExpanded,
        collapse,
        pendingAsk,
        ask,
        clearAsk: () => setPendingAsk(null),
        enabled: showChat,
      }}
    >
      <div className="flex h-svh overflow-hidden bg-background text-foreground">
        <PrimaryNav />
        <main className="relative flex min-w-0 flex-1 flex-col overflow-hidden">
          <MobileBar />
          <BreadcrumbProvider>
            <AppHeader />
            <div className="relative flex-1 overflow-y-auto [overflow-anchor:none]">
              {children}
              {showChat && <ChatPanel variant="floating" />}
            </div>
          </BreadcrumbProvider>
        </main>
      </div>
    </ChatExpandCtx.Provider>
  );
}
